import { logger } from "@/lib/logger";
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Copy, ExternalLink, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface FileGalleryProps {
  fileUrls: string[];
  compact?: boolean;
}

const isImageUrl = (url: string) => {
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
  const lowercaseUrl = url.toLowerCase();
  return imageExtensions.some((ext) => lowercaseUrl.includes(ext));
};

const getFileName = (url: string) => {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "file";
  } catch {
    return "file";
  }
};

// Extract the storage path from a public URL or return the path if it's already a path
const extractStoragePath = (url: string): string => {
  try {
    // If it's already a simple path (not a URL), return as-is
    if (!url.startsWith("http")) {
      return url;
    }
    
    // If it's a full URL, extract the path after the bucket name
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    // Find 'conversation-files' in the path and get everything after it
    const bucketIndex = pathParts.findIndex(part => part === "conversation-files");
    if (bucketIndex !== -1) {
      return pathParts.slice(bucketIndex + 1).join("/");
    }
    
    // Fallback: return the last parts of the path
    return pathParts.slice(-2).join("/");
  } catch {
    return url;
  }
};

export function FileGallery({ fileUrls, compact = false }: FileGalleryProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate signed URLs for all files
  useEffect(() => {
    const generateSignedUrls = async () => {
      if (!fileUrls || fileUrls.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const urlMap: Record<string, string> = {};
        
        for (const url of fileUrls) {
          const path = extractStoragePath(url);
          
          const { data, error: signError } = await supabase.storage
            .from("conversation-files")
            .createSignedUrl(path, 3600); // 1 hour expiry

          if (signError) {
            logger.error(`Failed to generate signed URL for ${path}:`, signError);
            // Fallback to original URL (will fail but shows error state)
            urlMap[url] = url;
          } else if (data?.signedUrl) {
            urlMap[url] = data.signedUrl;
          }
        }

        setSignedUrls(urlMap);
      } catch (err) {
        logger.error("Error generating signed URLs:", err);
        setError("Failed to load files. Please ensure you have admin access.");
      } finally {
        setLoading(false);
      }
    };

    generateSignedUrls();
  }, [fileUrls]);

  if (!fileUrls || fileUrls.length === 0) {
    return null;
  }

  const handleCopyUrl = async (originalUrl: string) => {
    const signedUrl = signedUrls[originalUrl] || originalUrl;
    await navigator.clipboard.writeText(signedUrl);
    toast({
      title: "Copied!",
      description: "Signed URL copied to clipboard (valid for 1 hour)",
    });
  };

  const handleDownload = (originalUrl: string) => {
    const signedUrl = signedUrls[originalUrl] || originalUrl;
    window.open(signedUrl, "_blank");
  };

  const getSignedUrl = (originalUrl: string) => signedUrls[originalUrl] || originalUrl;

  const imageFiles = fileUrls.filter(isImageUrl);
  const otherFiles = fileUrls.filter((url) => !isImageUrl(url));

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {imageFiles.length > 0 && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <ImageIcon className="h-4 w-4" />
            <span>{imageFiles.length}</span>
          </div>
        )}
        {otherFiles.length > 0 && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span>{otherFiles.length}</span>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading files...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Image thumbnails */}
        {imageFiles.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {imageFiles.map((url, index) => (
              <button
                key={index}
                onClick={() => setLightboxUrl(url)}
                className="group relative overflow-hidden rounded-md border border-border bg-muted transition-all hover:border-primary/50 hover:shadow-md"
              >
                <AspectRatio ratio={1}>
                  <img
                    src={getSignedUrl(url)}
                    alt={`Attachment ${index + 1}`}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                </AspectRatio>
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/20 group-hover:opacity-100">
                  <ExternalLink className="h-5 w-5 text-white drop-shadow-lg" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Other files list */}
        {otherFiles.length > 0 && (
          <div className="space-y-1">
            {otherFiles.map((url, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/50 px-3 py-2"
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm">{getFileName(url)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleCopyUrl(url)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleDownload(url)}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={!!lightboxUrl} onOpenChange={() => setLightboxUrl(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden p-0">
          {lightboxUrl && (
            <div className="relative">
              <img
                src={getSignedUrl(lightboxUrl)}
                alt="Full size attachment"
                className="max-h-[85vh] w-full object-contain"
              />
              <div className="absolute bottom-4 right-4 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCopyUrl(lightboxUrl)}
                >
                  <Copy className="mr-1 h-3 w-3" />
                  Copy URL
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDownload(lightboxUrl)}
                >
                  <Download className="mr-1 h-3 w-3" />
                  Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
